import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Spinner,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import {
  createSubscription,
  getSubscriptions,
  getSubscriptionBillings,
  getProducts,
  markSubscriptionAsPaid,
  freezeUnfreezeSubscription,
} from "store/license/actions";
import { updateSubscriptionBilling } from "store/subscriptions/actions";
import moment from "moment";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";

const Subscription = ({ t }) => {
  const dispatch = useDispatch();
  const { clientId } = useParams();
  const { clientDetails } = useSelector((state) => state.clientReducer);
  const {
    loading,
    subscriptions,
    billings,
    totalBillings,
    products,
    success,
    error,
  } = useSelector((state) => state.licenseReducer);
  const { updateBillingLoading } = useSelector((state) => state.licenseReducer);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [creatingSubscription, setCreatingSubscription] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentPeriod, setPaymentPeriod] = useState("month");
  const [modalPaymentPeriod, setModalPaymentPeriod] = useState("month");
  const [expiryDate, setExpiryDate] = useState(
    moment().add(1, "month").format("YYYY-MM-DD")
  );
  const [paymentExpiryDate, setPaymentExpiryDate] = useState(
    moment().add(1, "month").format("YYYY-MM-DD")
  );
  const [subscription, setSubscription] = useState({
    status: "Active",
    expiryDate: moment().add(1, "month").format("YYYY-MM-DD"),
  });
  const [freezeLoading, setFreezeLoading] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateModalPaymentPeriod, setUpdateModalPaymentPeriod] =
    useState("month");
  const [updateExpiryDate, setUpdateExpiryDate] = useState(
    moment().add(1, "month").format("YYYY-MM-DD")
  );
  const [selectedBilling, setSelectedBilling] = useState(null);

  function calculatePaymentPeriod(startDate, endDate) {
    if (!startDate || !endDate) return "Monthly";

    const start = moment(startDate);
    const end = moment(endDate);
    const diffInDays = end.diff(start, "days");

    if (diffInDays <= 35) {
      // ~1 month (allowing some flexibility)
      return t("Monthly");
    } else if (diffInDays <= 100) {
      // ~3 months
      return t("Quarterly");
    } else if (diffInDays <= 200) {
      // ~6 months
      return t("Half Yearly");
    } else if (diffInDays <= 400) {
      // ~1 year
      return t("Yearly");
    } else {
      return t("Monthly"); // Default fallback
    }
  }

  const fetchSubscriptions = () => {
    if (!clientDetails.licenseServiceId || !clientId) return;
    dispatch(getSubscriptions({ customerId: clientId, page, limit }));
  };

  const fetchBillings = (subscriptionId) => {
    console.log("f  etching billings");
    console.log(subscriptionId);
    if (!subscriptionId) return;
    dispatch(getSubscriptionBillings(subscriptionId, page, limit));
  };

  const fetchProducts = () => {
    dispatch(getProducts(1, 100));
    setProductsLoaded(true);
  };

  const generateExpiryDate = (period) => {
    const now = moment();
    switch (period) {
      case "month":
        return now.add(1, "month").format("YYYY-MM-DD");
      case "quarter":
        return now.add(3, "months").format("YYYY-MM-DD");
      case "half-year":
        return now.add(6, "months").format("YYYY-MM-DD");
      case "year":
        return now.add(1, "year").format("YYYY-MM-DD");
      default:
        return now.add(1, "month").format("YYYY-MM-DD");
    }
  };

  const handlePaymentPeriodChange = (e) => {
    const selectedPeriod = e.target.value;
    setPaymentPeriod(selectedPeriod);
    setExpiryDate(generateExpiryDate(selectedPeriod));
  };

  const handleModalPaymentPeriodChange = (e) => {
    const selectedPeriod = e.target.value;
    setModalPaymentPeriod(selectedPeriod);
    setPaymentExpiryDate(generateExpiryDate(selectedPeriod));
  };

  const handleCreateSubscription = () => {
    setCreatingSubscription(true);
    const productId =
      products && products.length > 0 ? products[0]._id : "default";

    // Create FormData for file upload
    const payload = {
      productId,
      expiryDate,
    };

    dispatch(createSubscription(clientId, payload));
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [clientId, page, limit, clientDetails.licenseServiceId]);

  useEffect(() => {
    if (subscriptions && subscriptions.length > 0) {
      setSubscription({
        ...subscriptions[0],
        status:
          new Date(subscriptions[0].expiryDate) > new Date()
            ? "Active"
            : "Inactive",
      });
      fetchBillings(subscriptions[0]._id);
    }
  }, [subscriptions]);

  useEffect(() => {
    if (!productsLoaded) {
      fetchProducts();
    }
  }, [productsLoaded]);

  useEffect(() => {
    if (success === "Subscription created successfully") {
      setCreatingSubscription(false);
      // Refresh subscriptions after creation
      setTimeout(() => {
        fetchSubscriptions();
      }, 1000);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      setCreatingSubscription(false);
    }
  }, [error]);

  useEffect(() => {
    const start = moment(billings[0]?.paymentDate);
    const end = moment(billings[0]?.expiryDate);
    const diffInDays = end.diff(start, "days");

    if (diffInDays <= 35) {
      // ~1 month (allowing some flexibility)
      setUpdateModalPaymentPeriod("month");
    } else if (diffInDays <= 100) {
      // ~3 months
      setUpdateModalPaymentPeriod("quarter");
    } else if (diffInDays <= 200) {
      // ~6 months
      setUpdateModalPaymentPeriod("half-year");
    } else if (diffInDays <= 400) {
      // ~1 year
      setUpdateModalPaymentPeriod("year");
    } else {
      setUpdateModalPaymentPeriod("month"); // Default fallback
    }
  }, [billings]);

  useEffect(() => {
    setUpdateExpiryDate(generateExpiryDate(updateModalPaymentPeriod));
  }, [updateModalPaymentPeriod]);

  const renderCreateSubscriptionButton = () => {
    return (
      <Card>
        <CardBody>
          <h4 className="card-title mb-4">{t("Create New Subscription")}</h4>
          <Form>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="paymentPeriod">{t("Payment Period")}</Label>
                  <Input
                    type="select"
                    name="paymentPeriod"
                    id="paymentPeriod"
                    value={paymentPeriod}
                    onChange={handlePaymentPeriodChange}
                    required
                  >
                    <option value="month">{t("Monthly")}</option>
                    <option value="quarter">{t("Quarterly")}</option>
                    <option value="half-year">{t("Half Yearly")}</option>
                    <option value="year">{t("Yearly")}</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label>{t("Expiry Date")}</Label>
                  <div className="form-control-plaintext">
                    {moment(expiryDate).format("MMMM DD, YYYY")}
                  </div>
                  <small className="form-text text-muted">
                    {t(
                      "Expiry date is automatically calculated based on payment period"
                    )}
                  </small>
                </FormGroup>
              </Col>
            </Row>
            <div className="text-center mt-4">
              <Button
                color="primary"
                onClick={handleCreateSubscription}
                disabled={creatingSubscription}
              >
                {creatingSubscription ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t("Creating...")}
                  </>
                ) : (
                  t("Create subscription")
                )}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    );
  };

  const renderSubscriptionDetails = () => {
    return (
      <>
        <Card>
          <CardBody>
            <h4 className="card-title mb-4">{t("Subscription Details")}</h4>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <h5 className="font-size-15">{t("Status")}:</h5>
                  <span
                    className={`badge ${subscription.status == "Active" ? `bg-success` : `bg-danger`} font-size-12`}
                  >
                    {subscription.status}
                  </span>
                </div>
              </Col>
              <Col md="6">
                <div className="mb-3">
                  <h5 className="font-size-15">{t("Freezing Status")}:</h5>
                  <span
                    className={`badge ${subscription.isFreezed ? `bg-warning` : `bg-info`} font-size-12`}
                  >
                    {subscription.isFreezed ? t("Frozen") : t("Active")}
                  </span>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <h5 className="font-size-15">{t("Next Billing")}:</h5>
                  <p className="text-muted mb-0">
                    {moment(subscription.expiryDate).format("MMMM DD, YYYY")}
                  </p>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <h5 className="font-size-15">{t("Payment Period")}:</h5>
                  <p className="text-muted mb-0">
                    {calculatePaymentPeriod(
                      subscription.createdAt,
                      subscription.expiryDate
                    )}
                  </p>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <h5 className="font-size-15">{t("License ID")}:</h5>
                  <p className="text-muted mb-0">
                    {clientDetails.licenseServiceId}
                  </p>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="card-title mb-0">{t("Billing History")}</h4>
              <Button
                color="primary"
                size="sm"
                disabled={!isPaymentButtonEnabled()}
                onClick={() => setPaymentModalOpen(true)}
                className="me-2"
              >
                Add Payment
              </Button>
              <Button
                color={subscription.isFreezed ? "success" : "warning"}
                onClick={() => handleFreezeUnfreeze(subscription)}
                disabled={freezeLoading}
              >
                {freezeLoading ? <Spinner size="sm" className="me-1" /> : null}
                {subscription.isFreezed ? "Unfreeze" : "Freeze"}
              </Button>
            </div>
            {loading ? (
              <div className="text-center my-4">
                <Spinner color="primary" />
              </div>
            ) : (billings || []).length > 0 ? (
              <div className="table-responsive">
                <Table className="table table-centered table-nowrap mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t("Creation Date")}</th>
                      <th>{t("Next billing")}</th>
                      <th>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(billings || []).map((billing, index) => (
                      <tr key={index}>
                        <td>
                          {moment(billing.createdAt).format("MMMM DD, YYYY")}
                        </td>
                        <td>
                          {moment(billing.expiryDate).format("MMMM DD, YYYY")}
                        </td>
                        <td>
                          {index === 0 && (
                            <UncontrolledDropdown>
                              <DropdownToggle
                                className="btn btn-sm btn-light"
                                caret={false}
                              >
                                <i className="mdi mdi-dots-horizontal"></i>
                              </DropdownToggle>
                              <DropdownMenu>
                                <DropdownItem
                                  onClick={() => handleOpenUpdateModal(billing)}
                                >
                                  <i className="mdi mdi-pencil me-2"></i>
                                  {t("Update")}
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center my-4">
                <p>{t("No billing history available")}</p>
              </div>
            )}
          </CardBody>
        </Card>
      </>
    );
  };

  const isPaymentButtonEnabled = () => {
    if (!subscriptions || subscriptions.length === 0) {
      return false;
    }

    const subscription = subscriptions[0]; // Check the first subscription as per requirements
    if (!subscription.expiryDate) {
      return false;
    }

    const today = moment();
    const expiryDate = moment(subscription.expiryDate);
    const notificationPeriod = subscription.notificationPeriod || 30; // Default to 30 days if not specified

    // Enable button if subscription is expired or close to expiry
    return (
      expiryDate.isBefore(today) ||
      expiryDate.diff(today, "days") <= notificationPeriod
    );
  };

  const handleMarkAsPaid = () => {
    const formData = new FormData();

    if (subscriptions && subscriptions.length > 0) {
      dispatch(
        markSubscriptionAsPaid(clientId, subscriptions[0]._id, {
          expiryDate: paymentExpiryDate,
        })
      );
      setPaymentModalOpen(false);

      // Refresh subscription data after marking as paid
      setTimeout(() => {
        dispatch(getSubscriptions(clientId));
        if (subscriptions[0]._id) {
          dispatch(getSubscriptionBillings(subscriptions[0]._id));
        }
      }, 1000);
    }
  };

  const handleFreezeUnfreeze = async () => {
    setFreezeLoading(true);
    try {
      const payload = { is_freezed: !subscription.isFreezed };
      dispatch(
        freezeUnfreezeSubscription(
          clientId,
          subscription._id,
          payload,
          page,
          limit
        )
      );
    } catch (error) {
      console.error("Error updating subscription freeze status:", error);
    } finally {
      setFreezeLoading(false);
    }
  };

  const handleOpenUpdateModal = (billing) => {
    setSelectedBilling(billing);
    setUpdateModalOpen(true);
  };

  const handleUpdateModalPaymentPeriodChange = (e) => {
    const selectedPeriod = e.target.value;
    setUpdateModalPaymentPeriod(selectedPeriod);
    setUpdateExpiryDate(generateExpiryDate(selectedPeriod));
  };

  const handleUpdateBilling = () => {
    if (selectedBilling) {
      dispatch(
        updateSubscriptionBilling(selectedBilling._id, {
          customerId: clientId,
          expiryDate: new Date(updateExpiryDate),
        })
      );
      setUpdateModalOpen(false);

      // Refresh billing data after update
      setTimeout(() => {
        if (subscriptions && subscriptions.length > 0) {
          fetchBillings(subscriptions[0]._id);
        }
      }, 1000);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Row>
            <Col lg="12">
              {!clientDetails.licenseServiceId
                ? renderCreateSubscriptionButton()
                : renderSubscriptionDetails()}
            </Col>
          </Row>
        </div>
      </div>

      <Modal
        isOpen={paymentModalOpen}
        toggle={() => setPaymentModalOpen(!paymentModalOpen)}
      >
        <ModalHeader toggle={() => setPaymentModalOpen(!paymentModalOpen)}>
          {t("Add Payment")}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="modalPaymentPeriod">{t("Payment Period")}</Label>
            <Input
              type="select"
              name="modalPaymentPeriod"
              id="modalPaymentPeriod"
              value={modalPaymentPeriod}
              onChange={handleModalPaymentPeriodChange}
              required
            >
              <option value="month">{t("Monthly")}</option>
              <option value="quarter">{t("Quarterly")}</option>
              <option value="half-year">{t("Half Yearly")}</option>
              <option value="year">{t("Yearly")}</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>{t("Expiry Date")}</Label>
            <div className="form-control-plaintext">
              {moment(paymentExpiryDate).format("MMMM DD, YYYY")}
            </div>
            <small className="form-text text-muted">
              {t(
                "Expiry date is automatically calculated based on payment period"
              )}
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPaymentModalOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button color="primary" onClick={handleMarkAsPaid}>
            {t("Submit")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Update Billing Modal */}
      <Modal
        isOpen={updateModalOpen}
        toggle={() => setUpdateModalOpen(!updateModalOpen)}
      >
        <ModalHeader toggle={() => setUpdateModalOpen(!updateModalOpen)}>
          {t("Update Billing")}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="updateModalPaymentPeriod">{t("Payment Period")}</Label>
            <Input
              type="select"
              name="updateModalPaymentPeriod"
              id="updateModalPaymentPeriod"
              value={updateModalPaymentPeriod}
              onChange={handleUpdateModalPaymentPeriodChange}
              required
            >
              <option value="month">{t("Monthly")}</option>
              <option value="quarter">{t("Quarterly")}</option>
              <option value="half-year">{t("Half Yearly")}</option>
              <option value="year">{t("Yearly")}</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>{t("New Expiry Date")}</Label>
            <div className="form-control-plaintext">
              {moment(updateExpiryDate).format("MMMM DD, YYYY")}
            </div>
            <small className="form-text text-muted">
              {t(
                "Expiry date is automatically calculated based on payment period"
              )}
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setUpdateModalOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            color="primary"
            onClick={handleUpdateBilling}
            disabled={updateBillingLoading}
          >
            {updateBillingLoading ? <Spinner size="sm" /> : t("Update")}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default withTranslation()(Subscription);
